import classNames from 'classnames'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { textOnImageRevealAnimation } from './animations'
import { Section, WPImage, WPLink } from '@/types'

type TextOnImageProps = Section &
  Partial<{
    image: WPImage
    alignment: 'left' | 'right'
    image_shape: 'circle' | 'square'
    title: string
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    paragraph: string
    button: WPLink
  }>

const TextOnImage = ({
  image,
  title,
  title_variant,
  title_component,
  paragraph,
  button,
  className,
  ...props
}: TextOnImageProps) => {
  return (
    <Motion animation={textOnImageRevealAnimation}>
      {(motionRef) => (
        <section
          ref={motionRef}
          className={classNames('h-svh-vh relative w-full', className)}
          {...props}
        >
          <Container className="absolute left-1/2 top-0 h-full w-full -translate-x-1/2">
            <div
              className={classNames(
                'relative h-full w-full',
                'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-xs before:bg-blue before:md:rounded-md',
                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-xs after:bg-black/30 after:md:rounded-md'
              )}
            >
              <ImageParallax fill className="rounded-xs md:rounded-md">
                {(ref) => (
                  <Image
                    fill
                    ref={ref}
                    size="large"
                    loading="lazy"
                    source={image}
                    alt={image?.alt}
                    className="rounded-xs md:rounded-md"
                  />
                )}
              </ImageParallax>
            </div>
          </Container>
          <Container className="relative flex h-full w-full items-center">
            <Grid className="items-center gap-y-sm">
              <div className="col-span-2 md:col-span-4 md:col-start-2 lg:col-span-5 lg:col-start-7">
                <div className="grid gap-md px-[2rem] md:px-0">
                  {title ? (
                    <Typography
                      variant={title_variant}
                      component={title_component}
                      className="text-white"
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="body"
                      paragraph
                      className="text-white"
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {button ? (
                    <div data-motion-button>
                      <Button href={button?.url} link dark variant="contained">
                        {button?.title}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default TextOnImage
